import React from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Link } from '@material-ui/core';
import { useTypedSelector, useAppDispatch } from 'app/store';
import { cleanError, OrderError } from 'reducers/errorCatcher.reducer';
import { useTranslation, Trans } from 'react-i18next';

const ErrorModal = () => {
  const transitionDurationMilliseconds = 0;
  const { data, place } = useTypedSelector(state => state.error.error);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const handleClose = () => {
    dispatch(cleanError());
  };

  const getMessage = (errorData: any, place: string) => {
    const orderError = errorData as OrderError;

    if (orderError?.errors) {
      if (orderError.errors.connectivity) {
        return (
          <>
            {t(
              'billing:components.errorsModal.messages.connectivityError',
              'Failed to process your request. Please try again later.'
            )}
          </>
        );
      } else if (orderError.errors.insufficientFunds) {
        return orderError.errors.achInsufficientFunds ? (
          <>
            {t(
              'billing:components.errorsModal.messages.achInsufficientFundsError',
              'Unfortunately, there are insufficent funds to process this transaction. Please try a different payment method.'
            )}
          </>
        ) : (
          <>
            {t(
              'billing:components.errorsModal.messages.insufficientFundsError',
              'Unfortunately, there are insufficent funds to process this transaction. Please try a different card.'
            )}
          </>
        );
      } else if (orderError.errors.cardDeclined) {
        return orderError.errors.achDeclined ? (
          <>
            {t(
              'billing:components.errorsModal.messages.achDeclinedError',
              'Unfortunately, this payment method was not successful. You can try again or try a different method of payment.'
            )}
          </>
        ) : (
          <>
            {t(
              'billing:components.errorsModal.messages.cardDeclinedError',
              'Unfortunately, your card has declined this transaction. Please try a different card.'
            )}
          </>
        );
      } else if (orderError.errors.account) {
        return (
          <>
            {t(
              'billing:components.errorsModal.messages.accountError',
              'Following information has failed to be submitted:'
            )}{' '}
            {orderError.errors.account.join(', ')}
          </>
        );
      } else if (orderError.errors.order) {
        return (
          <>
            {orderError.errors.order.join(', ')}.{' '}
            {t('billing:components.errorsModal.messages.orderError', 'Please try again later.')}
          </>
        );
      } else if (orderError.errors.taxId) {
        return (
          <Trans i18nKey="billing:components.errorsModal.messages.taxIdError">
            The Company with this ID already exists. If you want to sign in to your existing account, please click{' '}
            <Link href="https://pecdata.com/">here</Link>
          </Trans>
        );
      } else if (orderError.errors.gatewayError) {
        return (
          <>
            {t(
              'billing:components.errorsModal.messages.gatewayError',
              'Unfortunately, we were unable to process this payment. Please try again or contact us.'
            )}
          </>
        );
      }
    }

    if (place === 'companyData/createAccount.rejected') {
      return (
        <>
          {t(
            'billing:components.errorsModal.messages.unknownError',
            'Unfortunately, there was an error processing your payment. Please contact us.'
          )}
        </>
      );
    }
    if (place === 'companyData/search.rejected') {
      return (
        <>
          {t(
            'billing:components.errorsModal.messages.unableToSearch',
            'Unable to search companies at this time. Please enter your details manually.'
          )}
        </>
      );
    }
    return <>{t('billing:components.errorsModal.messages.somethingWentWrong', 'Oops! Something went wrong.')}</>;
  };

  return (
    <Dialog
      open={!!data}
      onClose={handleClose}
      transitionDuration={transitionDurationMilliseconds}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">{t('billing:components.errorsModal.title', 'Error')}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">{getMessage(data, place)}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" autoFocus>
          {t('billing:components.errorsModal.btnOk', 'Ok')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ErrorModal;
